<template>
  <div class="discount">
    <div v-if="discount.limitedOffer" class="discount-limited-offer">
      <div>{{ $t('discount.limitedOffer.title') }}</div>
      <div v-if="discount.validUntil" class="valid-until"
        v-html="$t('discount.limitedOffer.validity', { validUntil: validUntilDays })">
      </div>
    </div>
    <div class="discount-body">
      <h3 class="discount-headline">
        <L10n :item="discount" l10nName="summary" name="benefitSummary" />
      </h3>
      <div class="discount-description-wrapper">
        <L10n :item="discount" name="description" class="discount-description" html />
        <div class="discount-age-restrictions"  v-if="discount.restrictions &&
          (discount.restrictions.minAge != null || discount.restrictions.maxAge != null)">
          <div class="description-label">{{ $t("discount.fields.restrictions.age") }}</div>
          <div> {{  getAgeRestrictions() }} </div>
        </div>
        <div class="discount-card-types">
          <div class="description-label">{{ $t("discount.fields.validFor") }}</div>
          <div
              class="card-type" v-bind:class="cardType" :key="cardType"
              v-for="cardType in discount.cardTypes"
          >
            <div class="card-type-icon" v-bind:class="cardType"></div>
            {{ cardType.toUpperCase() }}
          </div>
        </div>
        <div class="discount-countries" v-if="discount.countryGeoIds">
          <div class="description-label">{{ $t("discount.fields.countries") }}</div>
          <div class="country-list">{{ discountCountries }}</div>
        </div>
      </div>
      <div class="discount-buttons">
        <online-discount-button v-if="isOnlineDiscount && !discount.voucherRequired"
          :verificationRequired="discount.verificationRequired" :benefit="discount"
          :redirectUrl="discount.webUrl" :onClick="onOnlineButtonClick" />
        <button v-if="discount.voucherRequired" type="button" class="primary-button"
            @click="onGetVoucherClick">
          {{ $t("discount.buttons.getVoucher") }}
        </button>
      </div>
    </div>
    <voucher-wizard-dialog v-if="voucherWizardOpened"></voucher-wizard-dialog>
  </div>
</template>

<style scoped>
  @import '../../assets/css/button-style.css';

  .discount {
    display: flex;
    flex-direction: column;
    border: 2px solid #EAEDEF;
    background-color: #F7F9FA;
    border-radius: 12px;
    color: var(--secondary-color);
    font-size: 16px;
    margin-bottom: 16px;
  }

  .discount-body {
    padding: 16px;
    padding-top: 0;
  }

  .discount-description-wrapper {
    border: 2px solid #EAEDEF;
    background-color: white;
    border-radius: 12px;
    padding: 16px;
    padding-top: 0;
  }

  .discount-description {
    text-align: justify;
  }

  .discount-buttons {
    display: flex;
  }

  .discount-buttons .primary-button {
    margin-right: 16px;
    margin-bottom: 0;
  }

  .primary-button {
    margin: 16px 0 8px 0;
    max-width: 200px;
  }

  .discount-headline {
    margin: 12px 0;
    font-size: 18px;
  }

  .discount-description {
    margin: 16px 0 8px 0;
  }

  .discount-age-restrictions {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
  }

  .discount-card-types {
    display: flex;
    align-items: center;
  }

  .card-type {
    display: flex;
    margin-right: 8px;
  }

  .card-type-icon {
    border-radius: 50%;
    width: 16px;
    height: 16px;
    margin-right: 4px;
  }

  .card-type-icon.isic {
    background: #40b8b8;
  }

  .card-type.isic {
    color: #40b8b8;
  }

  .card-type.itic {
    color: #f37526;
  }

  .card-type-icon.itic {
    background: #f37526;
  }

  .card-type.iytc {
    color: #2c90dc;
  }

  .card-type-icon.iytc {
    background: #2c90dc;
  }

  .discount-limited-offer {
    display: flex;
    justify-content: space-between;
    background-color: #ed9e25;
    padding: 12px 16px;
    color: white;
    font-weight: bold;
    font-size: 16px;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    margin: -1px;
  }

  .valid-until {
    font-weight: normal;
  }

  .discount-countries {
    display: flex;
    margin-top: 8px;
  }

  .description-label {
    font-weight: bold;
    margin-right: 8px;
  }

</style>

<style>
  .discount-description a:hover {
    color: var(--primary-color);
  }
</style>

<script>
import { mapActions, mapState } from 'vuex';
import VoucherWizardDialog from '@/components/vouchers/VoucherWizardDialog.vue';
import { BenefitType } from '@/types';
import L10n from '../L10n.vue';
import OnlineDiscountButton from './OnlineDiscountButton.vue';

export default {
  name: 'discount',
  props: {
    discount: {
      type: BenefitType,
      required: true,
    },
  },
  methods: {
    onGetVoucherClick() {
      if (this.discount.mobileOnly) {
        this.openMobileOnlyDialog();
        return;
      }
      this.openVoucherDialog(this.discount.benefitId);
      this.addRouteParam({ key: 'redeemOffer', value: this.discount.benefitId });
    },
    onOnlineButtonClick() {
      if (this.discount.mobileOnly) {
        this.openMobileOnlyDialog();
        return;
      }
      this.openRedirectDialog(this.discount.benefitId);
      this.addRouteParam({ key: 'redirectOffer', value: this.discount.benefitId });
    },
    getAgeRestrictions() {
      const { minAge, maxAge } = this.discount.restrictions;

      if (minAge != null && maxAge != null) {
        return this.$t('discount.voucher.restrictions.ageRange', { minAge, maxAge });
      }

      if (minAge != null) {
        return this.$t('discount.voucher.restrictions.minAge', { minAge });
      }

      return this.$t('discount.voucher.restrictions.maxAge', { maxAge });
    },
    ...mapActions({
      openVoucherDialog: 'openVoucherDialog',
      openRedirectDialog: 'openRedirectDialog',
      openMobileOnlyDialog: 'openMobileOnlyDialog',
      redirect: 'redirectTo',
      addRouteParam: 'addRouteParam',
    }),
  },
  computed: {
    ...mapState({
      voucherWizardOpened: (state) => state.voucher.voucherWizardOpened,
      countries: (state) => state.country.list,
    }),
    validUntilDays() {
      const today = new Date();
      const validUntil = new Date(this.discount.validUntil);
      const timeDifference = validUntil.getTime() - today.getTime();
      return Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
    },
    discountCountries() {
      const countryNames = [];
      this.discount.countryGeoIds.forEach((countryGeoId) => {
        const country = this.countries.find((item) => item.countryGeoId === countryGeoId);
        if (country != null) {
          countryNames.push(country.name);
        }
      });
      return countryNames.sort().join(', ');
    },
    isOnlineDiscount() {
      return (this.discount.availableOnline || this.discount.type === 'ONLINE')
          && this.discount.webUrl;
    },
  },
  components: {
    L10n,
    VoucherWizardDialog,
    OnlineDiscountButton,
  },
};
</script>
